// The flags that gate Settings sections and palette entries: the workspace's
// own settings, plus `machines_enabled` and `connections_enabled`, which
// live behind their own routes (the machines switch, the connections list:
// both kept out of the settings response, which the iPhone app's contract
// fixes).
//
// Until each answers, the value this browser remembered for the workspace
// stands in (features/navigation/navFlags), so a Settings section or a
// palette entry does not appear a moment after the rest; both answers are
// written back.

import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { connectionsListQuery, useConnectionsSwitch } from "@/api/connections";
import { useMachineSwitch } from "@/api/machines";
import { useCustomerSettings, type CustomerSettingsFlag } from "@/api/customerSettings";
import { pickNavFlags, rememberConnectionsFlag, rememberMachinesFlag } from "@/features/navigation/navFlags";
import { useRememberedNav, useRememberLiveFlags } from "@/features/navigation/useNavFlags";

export type SettingsFlags = Partial<Record<CustomerSettingsFlag, boolean>>;

export function useSettingsFlags(customerId: string | null): SettingsFlags | undefined {
  const settings = useCustomerSettings(customerId).data;
  const machines = useMachineSwitch(customerId).data;
  const remembered = useRememberedNav(customerId)?.flags;
  const live = useMemo(() => pickNavFlags(settings), [settings]);
  useRememberLiveFlags(customerId, live);
  const machinesOn = machines ? machines.enabled === true : undefined;
  const connectionsOn = useConnectionsSwitch(customerId);
  useEffect(() => {
    if (customerId && machinesOn !== undefined) rememberMachinesFlag(customerId, machinesOn);
  }, [customerId, machinesOn]);
  useEffect(() => {
    if (customerId && connectionsOn !== undefined) rememberConnectionsFlag(customerId, connectionsOn);
  }, [customerId, connectionsOn]);
  return useMemo(() => {
    const base = live ?? remembered;
    if (!base) return undefined;
    return {
      ...base,
      machines_enabled: machinesOn ?? remembered?.machines_enabled === true,
      connections_enabled: connectionsOn ?? remembered?.connections_enabled === true,
    };
  }, [live, remembered, machinesOn, connectionsOn]);
}

/** Still waiting for the first answer of the list that says whether Connections is on, with nothing remembered in this browser (a failed read counts as answered). */
export function useConnectionsSwitchUnknown(customerId: string | null): boolean {
  const loading = useQuery({ ...connectionsListQuery(customerId), staleTime: 60_000 }).isLoading;
  const remembered = useRememberedNav(customerId)?.flags?.connections_enabled;
  return loading && remembered === undefined;
}
